'use client';

import { PortalLink, useCart } from '@akademi/site-builder-runtime';

/**
 * Add to basket, for this app's own product page.
 *
 * <p>Deliberately not the `AddToCartButton` site-builder block. That block is
 * built for an authored page and expects the site-builder runtime's i18n
 * context; rendering it here threw `NO_I18NEXT_INSTANCE` during SSR and took
 * the whole product page's stream down with it. This page owns its strings
 * through `messages`, like every other word on it.
 *
 * <p>It also takes the product id it already has rather than re-reading the
 * product by slug the way the block must — the page has just loaded it.
 *
 * <p>Renders nothing when no cart is mounted. A site that sells by enquiry has
 * no basket to add to, and an inert button explaining that is worse than the
 * enquiry route the page already offers.
 */
export function AddToBasket({
  productId,
  label,
  addedLabel,
  outOfStockLabel,
  errorLabel,
  cartHref = '/cart',
  outOfStock = false,
}: {
  productId: number;
  label: string;
  addedLabel: string;
  outOfStockLabel: string;
  errorLabel: string;
  cartHref?: string;
  outOfStock?: boolean;
}) {
  const cart = useCart();

  if (!cart || !cart.available) return null;

  const disabled = cart.busy || outOfStock;
  const cartQuantity = cart.cart?.lines
    .filter((line) => line.productId === productId && line.parentLineId == null)
    .reduce((total, line) => total + Number(line.quantity), 0) ?? 0;

  async function add() {
    if (!cart) return;
    await cart.add({ productId, quantity: '1' });
  }

  return (
    <div>
      {cartQuantity > 0 ? (
        <PortalLink
          href={cartHref}
          className="inline-flex min-h-13 w-full items-center justify-center rounded-[var(--radius-button)] bg-[var(--color-brand-600)] px-6 font-semibold text-[var(--color-text-on-brand)] shadow-[var(--shadow-md)] transition-[transform,filter] hover:-translate-y-0.5 hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transform-none motion-reduce:transition-none"
        >
          {addedLabel} ({cartQuantity})
        </PortalLink>
      ) : (
        <button
          type="button"
          onClick={() => void add()}
          disabled={disabled}
          className="inline-flex min-h-13 w-full items-center justify-center rounded-[var(--radius-button)] bg-[var(--color-brand-600)] px-6 font-semibold text-[var(--color-text-on-brand)] shadow-[var(--shadow-md)] transition-[transform,filter] hover:-translate-y-0.5 hover:brightness-105 disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transform-none motion-reduce:transition-none"
        >
          {outOfStock ? outOfStockLabel : label}
        </button>
      )}
      {cart.error ? (
        <p role="alert" className="mt-2 text-sm text-[var(--color-danger-600,#dc2626)]">
          {errorLabel}
        </p>
      ) : null}
    </div>
  );
}
